import React, { useState, useRef, useEffect } from 'react';
import {
    Layout, Card, Button, Spin, Alert, message,
    Typography, Flex
} from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import * as publicActions from 'store/actions/PublicActions';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const { Content, Header } = Layout;
const { Title, Text, Paragraph } = Typography;

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PublicCCView = ({ document, recipient, token }) => {
    const pdfContainerRef = useRef(null);

    // State
    const [pdfUrl, setPdfUrl] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [, setPdfLoading] = useState(true);
    const [pdfError, setPdfError] = useState(null);

    // Initialize PDF URL and mark as viewed
    useEffect(() => {
        if (document?.signed_document_file) {
            setPdfUrl(document.signed_document_file);
        } else if (document?.document_file_url || document?.document_file) {
            setPdfUrl(document.document_file_url || document.document_file);
        }

        // Mark document as viewed
        if (token) {
            publicActions.markDocumentViewed(token);
        }
    }, [document, token]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPdfLoading(false);
        setPdfError(null);
    };

    const onDocumentLoadError = (error) => {
        console.error("PDF load error:", error);
        setPdfLoading(false);
        setPdfError(error);
        message.error("Failed to load PDF document");
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
            {/* Simple Header */}
            <Header style={{
                background: '#fff',
                padding: '0 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        <MailOutlined style={{ marginRight: 8 }} />
                        Document Copy (CC)
                    </Title>
                    <Text type="secondary">
                        {document?.title || document?.name}
                    </Text>
                </div>
                <div>
                    <Text>
                        Viewing as: <strong>{recipient?.name}</strong>
                    </Text>
                </div>
            </Header>

            <Content style={{ padding: 24 }}>
                <Alert
                    message="Courtesy Copy"
                    description={
                        <div>
                            <Paragraph style={{ marginBottom: 0 }}>
                                You have received this document as a courtesy copy (CC). This is for your information only.
                                No action is required from you.
                            </Paragraph>
                        </div>
                    }
                    type="info"
                    showIcon
                    icon={<MailOutlined />}
                    style={{ marginBottom: 16 }}
                />

                {/* PDF Preview */}
                <Card>
                    <div
                        ref={pdfContainerRef}
                        style={{
                            backgroundColor: '#666',
                            padding: 20,
                            borderRadius: 8,
                            maxHeight: 'calc(100vh - 300px)',
                            overflowY: 'auto'
                        }}
                    >
                        <Flex justify="center">
                            {pdfError ? (
                                <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
                                    <p>Failed to load PDF</p>
                                </div>
                            ) : pdfUrl ? (
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onDocumentLoadError}
                                    loading={<Spin size="large" tip="Loading PDF..." />}
                                >
                                    {numPages > 0 && [...Array(numPages).keys()].map((_, index) => {
                                        const pageNumber = index + 1;
                                        return (
                                            <div key={pageNumber} style={{ marginBottom: 10 }}>
                                                <Page
                                                    pageNumber={pageNumber}
                                                    scale={zoomLevel}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                />
                                                <div style={{
                                                    textAlign: 'center',
                                                    backgroundColor: '#fff',
                                                    padding: '5px'
                                                }}>
                                                    Page {pageNumber} of {numPages}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Document>
                            ) : (
                                <div style={{ color: '#fff', padding: 40 }}>
                                    No PDF preview available
                                </div>
                            )}
                        </Flex>
                    </div>

                    {/* Zoom Controls */}
                    <Flex justify="center" style={{ marginTop: 16 }} gap={16} align="center">
                        <Button onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))}>
                            Zoom Out
                        </Button>
                        <span>Zoom: {Math.round(zoomLevel * 100)}%</span>
                        <Button onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.25))}>
                            Zoom In
                        </Button>
                    </Flex>
                </Card>

                <Card style={{ marginTop: 16 }}>
                    <Alert
                        message="No Action Required"
                        description="This document has been shared with you for informational purposes. You can close this window when you're done viewing."
                        type="success"
                        showIcon
                    />
                </Card>
            </Content>
        </Layout>
    );
};

export default PublicCCView;
